// import { gettext } from '@eventbrite/i18n';
import { gettext } from '@eventbrite/i18n';
import loadable from '@loadable/component';
import React from 'react';
import { StickyContainer } from 'react-sticky';
import { categoryCodeToReadableTag } from '../../../utils/categories';
import { LazyRenderer } from '../components/LazyRenderer';
import { NightlifeBackground } from '../components/NightlifeBackground';
import { NightlifeCuratedContainer } from '../components/NightlifeCuratedContainer';
import {
    EVENTBRITE_FORMAT_MAPPING,
    PARTY_EVENTBRITE_FORMAT,
    PERFORMANCE_EVENTBRITE_FORMAT,
} from '../constants/formats';
import { NIGHTLIFE_TAGS } from '../constants/nightLife';
import { CategoryBrowseBreadCrumb } from '../containers/CategoryBrowseBreadCrumb';
import { CategoryBrowseEvents } from '../containers/CategoryBrowseEvents';
import CategoryBrowseNearbyCities from '../containers/CategoryBrowseNearbyCities';
import { NightlifeCategoriesContainer } from '../containers/NightlifeCategoriesContainer';
import { NightlifeHeaderContainer } from '../containers/NightlifeHeaderContainer';
import { useEnvContext, useRouteContext, useSearchContext } from '../context';
import { useLocation } from '../hooks/queries/location';
import { NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT } from '../utils/analytics';
import './NightlifePage.scss';

const CategoryBrowseOrganizerTags = loadable(
    () => import('../containers/CategoryBrowseOrganizerTags'),
    {
        fallback: <div style={{ minHeight: '200px' }}></div>,
        ssr: false,
    },
);

const CategoryBrowseOrganizers = loadable(
    () => import('../containers/CategoryBrowseOrganizers'),
);

//TODO: Set date filter to leverage current future and date when both applied
export const NightlifePage: React.FunctionComponent = () => {
    // Env and General Data
    const envContext = useEnvContext();
    const { query } = useSearchContext();
    const queryActiveTags = query?.tags || NIGHTLIFE_TAGS;
    const isPartyCategory =
        query?.tags?.length === 1 && query.tags[0] === PARTY_EVENTBRITE_FORMAT;
    const isWeekendFilter = query?.dates?.includes('this_weekend');
    const user = envContext?.user;
    const locale = envContext?.env?.localeInfo?.locale || 'en_US';
    const gaSettings = envContext?.gaSettings;
    const isBotRequest = envContext?.isBotRequest;
    const { parsedValues, location } = useRouteContext();
    const location_data = useLocation();
    const activeTag = parsedValues?.activeTag || '';
    const date = parsedValues?.date;
    const customDate = parsedValues?.customDate;
    const routeContext = useRouteContext();
    const featureFlags = routeContext.featureFlags;
    const shouldRenderThisWeekend = () => {
        return date === 'current_future' && !isWeekendFilter && !customDate;
    };

    const showNightlifeCarousel =
        featureFlags?.curatedModuleCategoryBrowse || false;

    const renderEventCarousels = () => {
        return (
            <div className="event_carousel">
                <CategoryBrowseEvents
                    queryOverload={{
                        page_size: 8,
                        price: 'paid',
                    }}
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketKey="most_popular"
                    bucketPosition="2"
                    renderEmptyState
                    adSlots={[1]}
                />
                {shouldRenderThisWeekend() && (
                    <CategoryBrowseEvents
                        queryOverload={{
                            page_size: 8,
                            dates: ['this_weekend'],
                            price: 'paid',
                            aggs: {},
                        }}
                        title={gettext('This weekend')}
                        bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                        bucketKey="this_weekend"
                        bucketPosition="4"
                        lazyLoadCarouselElements
                    />
                )}
                {!isPartyCategory && (
                    <CategoryBrowseEvents
                        queryOverload={{
                            page_size: 8,
                            tags: [PARTY_EVENTBRITE_FORMAT, ...queryActiveTags],
                            price: 'paid',
                            aggs: {},
                        }}
                        title={
                            EVENTBRITE_FORMAT_MAPPING[PARTY_EVENTBRITE_FORMAT]
                        }
                        bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                        bucketKey={
                            categoryCodeToReadableTag(
                                PARTY_EVENTBRITE_FORMAT,
                            ) || PARTY_EVENTBRITE_FORMAT
                        }
                        bucketPosition="6"
                        forceRenderHeader={isBotRequest}
                        lazyLoadCarouselElements
                    />
                )}
                <CategoryBrowseEvents
                    forceRenderHeader={isBotRequest}
                    queryOverload={{
                        page_size: 8,
                        price: 'free',
                        aggs: {},
                    }}
                    title={gettext('Free')}
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketKey="free"
                    bucketPosition="5"
                    lazyLoadCarouselElements
                />
                <CategoryBrowseOrganizerTags />

                <CategoryBrowseEvents
                    bucketName={NIGHTLIFE_CATEGORY_BROWSE_PLACEMENT}
                    bucketPosition="8"
                    queryOverload={{
                        page_size: 8,
                        tags: [
                            PERFORMANCE_EVENTBRITE_FORMAT,
                            ...queryActiveTags,
                        ],
                        price: 'paid',
                        aggs: {},
                    }}
                    title={
                        EVENTBRITE_FORMAT_MAPPING[PERFORMANCE_EVENTBRITE_FORMAT]
                    }
                    bucketKey={
                        categoryCodeToReadableTag(
                            PERFORMANCE_EVENTBRITE_FORMAT,
                        ) || PERFORMANCE_EVENTBRITE_FORMAT
                    }
                    forceRenderHeader={isBotRequest}
                    lazyLoadCarouselElements
                />
                <CategoryBrowseOrganizers
                    user={user}
                    gaSettings={gaSettings}
                    location={location}
                />
                <CategoryBrowseNearbyCities customClassName="near_by_cities" />
            </div>
        );
    };

    // todo update parties and  today , weekend
    return (
        <div className="nightlife">
            <NightlifeBackground>
                <CategoryBrowseBreadCrumb />

                <main className="nightlife__main">
                    <NightlifeHeaderContainer
                        activeCategory={activeTag}
                        locale={locale}
                    />
                    <StickyContainer style={{ overflowY: 'auto' }}>
                        <LazyRenderer
                            numChildInitialRender={isBotRequest ? -1 : 2}
                        >
                            <div className="nightlife-horizontal-bar"></div>
                            <NightlifeCategoriesContainer />

                            {showNightlifeCarousel && (
                                <NightlifeCuratedContainer
                                    holiday="EOY"
                                    isAuthenticated={
                                        envContext?.user?.isAuthenticated ||
                                        false
                                    }
                                    isMobile={
                                        typeof window !== 'undefined' &&
                                        window?.innerWidth <= 768
                                            ? true
                                            : false
                                    }
                                    browseSurface="Nightlife"
                                    placeId={location_data.data?.placeId || ''}
                                />
                            )}
                            {renderEventCarousels()}
                        </LazyRenderer>
                    </StickyContainer>
                </main>
            </NightlifeBackground>
        </div>
    );
};

export default NightlifePage;
